import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import PendingIcon from '@mui/icons-material/Pending';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { t, TFunction } from 'i18next';

import { Icon } from '@eon-home/react-library';

import {
    CustomerGatewayResponseModel,
    GatewayDeviceConfigurationScanningStateEnum,
} from '@swagger-http';
import { GatewayDeviceInstallation } from '@swagger-http/dashboard';

import { DeviceId } from '@containers/customer/smart-energy/tables/components';
import { GatewayAction } from '@containers/customer/smart-energy/tables/gateway-table/components';
import { Column } from '@tools/types/table';

import { formatDate } from '@tools/utils';

import GatewayIcon from '@assets/icons/icon_gateway.svg';

import styles from '@containers/customer/smart-energy/tables/components.module.scss';
import unallocatedInstallationsStyles from '@containers/unallocated-installations/index.module.scss';
const { Done, Failed, Pending } = GatewayDeviceConfigurationScanningStateEnum;

export const getGatewayScanningStateIcon = (state?: string) => {
    switch (state) {
        case Done:
            return (
                <CheckCircleIcon
                    color="success"
                    className={[
                        unallocatedInstallationsStyles['scanning-status-icon'],
                    ].join(' ')}
                />
            );

        case Failed:
            return (
                <ErrorIcon
                    color="error"
                    className={
                        unallocatedInstallationsStyles['scanning-status-icon']
                    }
                />
            );

        case Pending:
            return (
                <PendingIcon
                    color="warning"
                    className={
                        unallocatedInstallationsStyles['scanning-status-icon']
                    }
                />
            );

        default:
            // neutral icon, like for example when no deletion attempt is made
            return (
                <PanoramaFishEyeIcon
                    color="disabled"
                    className={
                        unallocatedInstallationsStyles['scanning-status-icon']
                    }
                />
            );
    }
};

export const getGatewayColumns = (t: TFunction): Column[] => [
    {
        key: 'type',
        label: t('customer.pvBatteryTable.header.type'),
        visible: true,
        allowOrder: false,
        renderCellComponent: () => (
            <Box className={styles.wbIcon}>
                <Icon src={GatewayIcon} />
            </Box>
        ),
    },
    {
        key: 'deviceId',
        label: t('customer.pvBatteryTable.header.deviceId'),
        visible: true,
        allowOrder: false,
        renderCellComponent: (props: CustomerGatewayResponseModel) => (
            <DeviceId deviceId={props?.deviceId} />
        ),
    },
    {
        key: 'vendorName',
        label: t('customer.pvBatteryTable.header.vendorName'),
        visible: true,
        allowOrder: false,
    },
    {
        key: 'scanningState',
        label: t('customer.smartHomeTable.header.scanningState'),
        visible: true,
        allowOrder: false,
        getValue: ({ deviceConfiguration }: GatewayDeviceInstallation) =>
            deviceConfiguration?.scanningState || '',
    },
    {
        key: 'lastScanDate',
        label: t('customer.smartHomeTable.header.lastScanDate'),
        visible: true,
        allowOrder: false,
        renderCellComponent: ({
            deviceConfiguration,
        }: GatewayDeviceInstallation) => (
            <Typography
                noWrap
                variant="inherit"
                sx={{
                    mr: 8,
                }}
            >
                {deviceConfiguration?.lastScanDate
                    ? formatDate(
                          deviceConfiguration?.lastScanDate,
                          'LL HH:mm:ss',
                      )
                    : ''}
            </Typography>
        ),
    },
    {
        key: 'action',
        label: '',
        type: 'action',
        allowOrder: false,
        renderCellComponent: (props: GatewayDeviceInstallation) => (
            <GatewayAction {...props} />
        ),
    },
];

enum GatewayErrorCode {
    GridBoxAlreadyPaired = 'GridBoxAlreadyPaired',
    InvalidStartCode = 'InvalidStartCode',
    NotFoundStartCode = 'NotFoundStartCode',
    ConfigurationNotAdded = 'ConfigurationNotAdded',
    CustomerNotFound = 'CustomerNotFound',
    InvalidSerialNumber = 'InvalidSerialNumber',
    InvalidSiteId = 'InvalidSiteId',
    VendorNotFound = 'VendorNotFound',
    UnsupportedDeviceType = 'UnsupportedDeviceType',
    OverwriteGatewayError = 'OverwriteGatewayError',
    ConfigurationNotDeleted = 'ConfigurationNotDeleted',
    ConfigurationNotUpdated = 'ConfigurationNotUpdated',
    InvalidCustomerNumber = 'InvalidCustomerNumber',
    OverwriteInverterKindError = 'OverwriteInverterKindError',
    UnsupportedConfiguration = 'UnsupportedConfiguration',
    TimeOfUseNotReady = 'TimeOfUseNotReady',
    DuplicatedInstallationCustomerSite = 'DuplicatedInstallationCustomerSite',
    DuplicatedInstallation = 'DuplicatedInstallation',
}

export const gatewayErrorTranslations: Record<GatewayErrorCode, string> = {
    [GatewayErrorCode.GridBoxAlreadyPaired]: t(
        'unallocatedInstallation.gatewayErrors.gridBoxAlreadyPaired',
    ),
    [GatewayErrorCode.InvalidStartCode]: t(
        'unallocatedInstallation.gatewayErrors.invalidStartCode',
    ),
    [GatewayErrorCode.NotFoundStartCode]: t(
        'unallocatedInstallation.gatewayErrors.notFoundStartCode',
    ),
    [GatewayErrorCode.ConfigurationNotAdded]: t(
        'unallocatedInstallation.gatewayErrors.configurationNotAdded',
    ),
    [GatewayErrorCode.CustomerNotFound]: t(
        'unallocatedInstallation.gatewayErrors.customerNotFound',
    ),
    [GatewayErrorCode.InvalidSerialNumber]: t(
        'unallocatedInstallation.gatewayErrors.invalidSerialNumber',
    ),
    [GatewayErrorCode.InvalidSiteId]: t(
        'unallocatedInstallation.gatewayErrors.invalidSiteId',
    ),
    [GatewayErrorCode.VendorNotFound]: t(
        'unallocatedInstallation.gatewayErrors.vendorNotFound',
    ),
    [GatewayErrorCode.UnsupportedDeviceType]: t(
        'unallocatedInstallation.gatewayErrors.unsupportedDeviceType',
    ),
    [GatewayErrorCode.OverwriteGatewayError]: t(
        'unallocatedInstallation.gatewayErrors.overwriteGatewayError',
    ),
    [GatewayErrorCode.ConfigurationNotDeleted]: t(
        'unallocatedInstallation.gatewayErrors.configurationNotDeleted',
    ),
    [GatewayErrorCode.ConfigurationNotUpdated]: t(
        'unallocatedInstallation.gatewayErrors.configurationNotUpdated',
    ),
    [GatewayErrorCode.InvalidCustomerNumber]: t(
        'unallocatedInstallation.gatewayErrors.invalidCustomerNumber',
    ),
    [GatewayErrorCode.OverwriteInverterKindError]: t(
        'unallocatedInstallation.gatewayErrors.overwriteInverterKindError',
    ),
    [GatewayErrorCode.UnsupportedConfiguration]: t(
        'unallocatedInstallation.gatewayErrors.unsupportedConfiguration',
    ),
    [GatewayErrorCode.TimeOfUseNotReady]: t(
        'unallocatedInstallation.gatewayErrors.timeOfUseNotReady',
    ),
    [GatewayErrorCode.DuplicatedInstallationCustomerSite]: t(
        'unallocatedInstallation.gatewayErrors.duplicatedInstallationCustomerSite',
    ),
    [GatewayErrorCode.DuplicatedInstallation]: t(
        'unallocatedInstallation.gatewayErrors.duplicatedInstallation',
    ),
};

export const getTranslatedGatewayErrorMessage = (
    errorCode: GatewayErrorCode,
): string =>
    gatewayErrorTranslations[errorCode] ??
    t('unallocatedInstallation.gatewayErrors.unknown');

export const isGatewayErrorCode = (value: string): value is GatewayErrorCode =>
    Object.keys(GatewayErrorCode).includes(value as GatewayErrorCode);
